import React, { useState, useEffect } from 'react'
import styled from 'styled-components'
import { Input } from '../../atoms/Input'
import { Button } from '../../atoms/Button'
import { Text } from '../../atoms/Text'
import { HeightInputModalProps } from './MapDraw.types'

const ModalOverlay = styled.div<{ $isOpen: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: ${({ $isOpen }) => ($isOpen ? 'flex' : 'none')};
  align-items: center;
  justify-content: center;
  z-index: 9999;
`

const ModalContainer = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-radius: 10px;
  padding: 20px;
  max-width: 320px;
  width: 90%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: ${({ theme }) => theme.shadows.xl};
`

const ModalHeader = styled.div`
  margin-bottom: 16px;
`

const ModalBody = styled.div`
  margin-bottom: 20px;
`

const ModalFooter = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
`

const InputGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

const InputLabel = styled.label`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.secondary};
`

const HeightUnit = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  margin-left: 6px;
`

export const HeightInputModal: React.FC<HeightInputModalProps> = ({
  isOpen,
  onConfirm,
  onCancel,
  defaultHeight = 10,
  minHeight = 1,
  maxHeight = 1000,
}) => {
  const [height, setHeight] = useState<string>(defaultHeight.toString())
  const [error, setError] = useState<string>('')

  useEffect(() => {
    if (isOpen) {
      setHeight(defaultHeight.toString())
      setError('')
    }
  }, [isOpen, defaultHeight])

  const handleHeightChange = (value: string) => {
    setHeight(value)

    // Validate input
    const numValue = parseFloat(value)
    if (value === '' || isNaN(numValue)) {
      setError('')
    } else if (numValue < minHeight) {
      setError(`Height must be at least ${minHeight}m`)
    } else if (numValue > maxHeight) {
      setError(`Height cannot exceed ${maxHeight}m`)
    } else {
      setError('')
    }
  }

  const handleConfirm = () => {
    const numValue = parseFloat(height)
    if (isNaN(numValue) || numValue < minHeight || numValue > maxHeight) {
      setError('Please enter a valid height value')
      return
    }

    onConfirm(numValue)
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !error && height) {
      handleConfirm()
    } else if (e.key === 'Escape') {
      onCancel()
    }
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onCancel()
    }
  }

  if (!isOpen) return null

  return (
    <ModalOverlay $isOpen={isOpen} onClick={handleOverlayClick}>
      <ModalContainer>
        <ModalHeader>
          <Text variant="h3" color="primary">
            Set Building Height
          </Text>
          <Text variant="body2" color="secondary">
            Specify the height for the 3D polygon extrusion
          </Text>
        </ModalHeader>

        <ModalBody>
          <InputGroup>
            <InputLabel htmlFor="height-input">
              Height <HeightUnit>(meters)</HeightUnit>
            </InputLabel>
            <Input
              id="height-input"
              type="number"
              value={height}
              onChange={(e) => handleHeightChange(e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder="Enter height in meters"
              min={minHeight}
              max={maxHeight}
              step="0.1"
              autoFocus
              error={!!error}
            />
            {error && (
              <Text variant="caption" color="error">
                {error}
              </Text>
            )}
          </InputGroup>
        </ModalBody>

        <ModalFooter>
          <Button variant="secondary" size="small" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="small"
            onClick={handleConfirm}
            disabled={!!error || !height}
          >
            Confirm
          </Button>
        </ModalFooter>
      </ModalContainer>
    </ModalOverlay>
  )
}
